import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Box, Typography, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import AccordionTransactionModal from './AccordionTransactionModal';
import UnifiedTransactionTable from '../shared/UnifiedTransactionTable';
import ReceiptGenerator from '../receipts/ReceiptGenerator';
import { useTransactionAutoUpdate } from '../../hooks/useTransactionAutoUpdate';
import { VALID_MTOS, generateTransactionId } from '../../utils/constants';
interface Transaction {
  id: string;
  senderName: string;
  receiverName: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  date: string;
  status: string;
  paymentMethod: string;
  country: string;
  mto: string;
  type: string;
  branchCode: string;
  agentCode?: string;
  createdAt?: string;
  currentStage: string;
  lifecycle: string;
  transactionStatus: string;
  systemStatus: string;
}
const TransactionHistoryPage: React.FC = () => {
  useTransactionAutoUpdate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [receiptType, setReceiptType] = useState<'ePRC' | 'MoneyGram' | 'BankReceipt'>('ePRC');
  const [receiptMenuAnchor, setReceiptMenuAnchor] = useState<null | HTMLElement>(null);
  const getBranchCode = () => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.branchCode || 'BR001';
    }
    return 'BR001';
  };
  const getUserType = () => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.userType || 'branch';
    }
    return 'branch';
  };
  const getAgentCode = () => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.agentCode || 'BIPL';
    }
    return 'BIPL';
  };
  // Generate random transactions for demo
  useEffect(() => {
    const generateRandomTransactions = () => {
      setIsLoading(true);
      const statuses = ['Completed', 'Rejected', 'Cancelled', 'Pending', 'In-Process'];
      const currencies = ['USD', 'EUR', 'GBP', 'USD', 'CAD', 'CHF'];
      const branchCode = getBranchCode();
      const userType = getUserType();
      const agentCode = getAgentCode();
      const stages = ['Screening', 'Compliance Check', 'Verification', 'Processing', 'Finalization'];
      const lifecycles = ['IN PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED'];
      const transactionStatuses = ['FUNDS TRANSFERRED', 'PENDING TRANSFER', 'TRANSFER FAILED', 'CANCELLED', 'AWAITING APPROVAL'];
      const systemStatuses = ['IN PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED', 'PENDING'];
      const africanSenders = ['Amara Diallo', 'Kwame Asante', 'Fatima Bello', 'Chidi Okonkwo', 'Ngozi Adeyemi', 'Kofi Boateng', 'Yusuf Hassan', 'Aisha Kamara', 'Emeka Obi', 'Ama Serwaa'];
      const africanReceivers = ['Abena Mensah', 'Yusuf Hassan', 'Ngozi Adeyemi', 'Kofi Boateng', 'Fatima Bello', 'Kwame Asante', 'Amara Diallo', 'Chidi Okonkwo', 'Aisha Kamara', 'Emeka Obi'];
      const africanCountries = ['Nigeria', 'Ghana', 'Kenya', 'Senegal', "Côte d'Ivoire", 'Tanzania'];
      const paymentMethods = ['Cash', 'Bank', 'Wallet'];
      const randomTransactions: Transaction[] = Array.from({
        length: 40
      }, (_, i) => {
        const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
        const randomMto = VALID_MTOS[Math.floor(Math.random() * VALID_MTOS.length)];
        const randomCurrency = currencies[Math.floor(Math.random() * currencies.length)];
        const randomAmount = Math.floor(Math.random() * 10000) + 500;
        const randomPayoutAmount = Math.floor(randomAmount * 1550);
        const today = new Date();
        const threeMothsAgo = new Date();
        threeMothsAgo.setMonth(today.getMonth() - 3);
        const randomDate = new Date(threeMothsAgo.getTime() + Math.random() * (today.getTime() - threeMothsAgo.getTime()));
        const createdDate = new Date(randomDate);
        createdDate.setHours(Math.floor(Math.random() * 24));
        createdDate.setMinutes(Math.floor(Math.random() * 60));
        let txnBranchCode: string;
        let txnAgentCode: string;
        if (userType === 'agent') {
          txnBranchCode = branchCode;
          txnAgentCode = agentCode;
        } else {
          txnBranchCode = branchCode;
          txnAgentCode = 'BIPL';
        }
        const transactionId = generateTransactionId(randomMto, i + 1000010);
        const randomStage = stages[Math.floor(Math.random() * stages.length)];
        const randomLifecycle = lifecycles[Math.floor(Math.random() * lifecycles.length)];
        const randomTransactionStatus = transactionStatuses[Math.floor(Math.random() * transactionStatuses.length)];
        const randomSystemStatus = systemStatuses[Math.floor(Math.random() * systemStatuses.length)];
        const randomSender = africanSenders[Math.floor(Math.random() * africanSenders.length)];
        const randomReceiver = africanReceivers[Math.floor(Math.random() * africanReceivers.length)];
        const randomCountry = africanCountries[Math.floor(Math.random() * africanCountries.length)];
        const randomPaymentMethod = paymentMethods[Math.floor(Math.random() * paymentMethods.length)];
        return {
          id: transactionId,
          senderName: randomSender,
          receiverName: randomReceiver,
          amount: randomAmount,
          currency: randomCurrency,
          payoutAmount: randomPayoutAmount,
          payoutCurrency: 'NGN',
          date: randomDate.toISOString().split('T')[0],
          status: randomStatus,
          paymentMethod: randomPaymentMethod,
          country: randomCountry,
          mto: randomMto,
          type: 'OTC',
          branchCode: txnBranchCode,
          agentCode: txnAgentCode,
          createdAt: createdDate.toISOString(),
          currentStage: randomStage,
          lifecycle: randomLifecycle,
          transactionStatus: randomTransactionStatus,
          systemStatus: randomSystemStatus
        };
      });
      setTransactions(randomTransactions);
      setIsLoading(false);
    };
    generateRandomTransactions();
  }, []);
  // Load initiated transactions from localStorage
  useEffect(() => {
    const loadInitiatedTransactions = () => {
      const stored = localStorage.getItem('initiatedTransactions');
      const branchCode = getBranchCode();
      const userType = getUserType();
      const agentCode = getAgentCode();
      if (stored) {
        const initiatedTransactions = JSON.parse(stored);
        const formattedTransactions: Transaction[] = initiatedTransactions.map((t: any) => {
          let txnBranchCode: string;
          let txnAgentCode: string;
          if (userType === 'agent') {
            txnBranchCode = t.branchCode || branchCode;
            txnAgentCode = t.agentCode || agentCode;
          } else {
            txnBranchCode = t.branchCode || branchCode;
            txnAgentCode = 'BIPL';
          }
          return {
            id: t.id || '',
            senderName: t.senderName || t.parties?.sender?.name || 'Unknown',
            receiverName: t.receiverName || t.parties?.receiver?.name || 'Unknown',
            amount: t.amount || t.amounts?.sendAmount || t.overview?.amount || 0,
            currency: t.currency || t.amounts?.sendCurrency || t.overview?.currency || 'USD',
            payoutAmount: t.payoutAmount || t.amounts?.payoutAmount || t.overview?.payoutAmount || 0,
            payoutCurrency: t.payoutCurrency || t.amounts?.payoutCurrency || t.overview?.payoutCurrency || 'NGN',
            date: t.date || t.overview?.date || new Date().toISOString().split('T')[0],
            status: t.status || t.overview?.status || '—',
            paymentMethod: t.paymentMethod || 'Cash',
            country: t.country || t.receiverCountry || 'Nigeria',
            mto: t.agent || t.mto || 'Unknown',
            type: 'OTC',
            branchCode: txnBranchCode,
            agentCode: txnAgentCode,
            createdAt: t.createdAt || t.timestamp || new Date().toISOString(),
            currentStage: t.currentStage || 'Initiated',
            lifecycle: t.lifecycle || 'Active',
            transactionStatus: t.transactionStatus || 'Pending',
            systemStatus: t.systemStatus || 'Processing'
          };
        });
        setTransactions(prevTransactions => {
          const transactionMap = new Map<string, Transaction>();
          formattedTransactions.forEach(t => {
            transactionMap.set(t.id, t);
          });
          prevTransactions.forEach(t => {
            if (!transactionMap.has(t.id)) {
              transactionMap.set(t.id, t);
            }
          });
          return Array.from(transactionMap.values());
        });
      }
    };
    loadInitiatedTransactions();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'initiatedTransactions') {
        loadInitiatedTransactions();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('transactionAdded', loadInitiatedTransactions);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('transactionAdded', loadInitiatedTransactions);
    };
  }, []);
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  const viewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransactionId(transaction.id);
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };
  const handleReceiptMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => {
    setReceiptMenuAnchor(event.currentTarget);
    setSelectedTransaction(transaction);
  };
  const handleReceiptMenuClose = () => {
    setReceiptMenuAnchor(null);
  };
  const handleGenerateReceipt = (type: 'ePRC' | 'MoneyGram' | 'BankReceipt') => {
    setReceiptType(type);
    setShowReceiptModal(true);
    handleReceiptMenuClose();
  };
  // Check if MTO is MoneyGram
  const isMoneyGramTransaction = (mto: string) => {
    return mto.toLowerCase().includes('moneygram');
  };
  // Check if Bank Receipt is available for this MTO
  const isBankReceiptAvailable = (mto: string) => {
    const bankReceiptMTOs = ['ria money transfer', 'ime', 'international money express', 'instantcash', 'express money', 'jingle pay'];
    return bankReceiptMTOs.some(availableMTO => mto.toLowerCase().includes(availableMTO.toLowerCase()));
  };
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Cancelled':
        return 'default';
      case 'Pending':
        return 'warning';
      case 'In-Process':
        return 'info';
      default:
        return 'default';
    }
  };
  const columns = [{
    id: 'id',
    label: 'Transaction ID',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'date',
    label: 'Date',
    filterable: true,
    filterType: 'date' as const
  }, {
    id: 'amount',
    label: 'Amount FCY',
    filterable: true,
    filterType: 'number' as const,
    format: (value: number, row: Transaction) => formatCurrency(value, row.currency)
  }, {
    id: 'payoutAmount',
    label: 'Amount LCY',
    format: (value: number, row: Transaction) => formatCurrency(value, row.payoutCurrency)
  }, {
    id: 'mto',
    label: 'MTO',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: Array.from(VALID_MTOS)
  }, {
    id: 'currentStage',
    label: 'Current Stage',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['Screening', 'Compliance Check', 'Verification', 'Processing', 'Finalization']
  }, {
    id: 'lifecycle',
    label: 'Lifecycle',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['IN PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED'],
    format: (value: string) => <Chip label={value} color={value === 'COMPLETED' ? 'success' : value === 'IN PROGRESS' ? 'info' : value === 'FAILED' ? 'error' : 'default'} size="small" data-id="element-661" />
  }, {
    id: 'transactionStatus',
    label: 'Transaction Status',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['FUNDS TRANSFERRED', 'PENDING TRANSFER', 'TRANSFER FAILED', 'CANCELLED', 'AWAITING APPROVAL'],
    format: (value: string) => <Chip label={value} color={value === 'FUNDS TRANSFERRED' ? 'success' : value === 'PENDING TRANSFER' ? 'warning' : value === 'AWAITING APPROVAL' ? 'info' : value === 'TRANSFER FAILED' ? 'error' : 'default'} size="small" data-id="element-662" />
  }, {
    id: 'systemStatus',
    label: 'Status',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['IN PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED', 'PENDING'],
    format: (value: string) => <Chip label={value} color={value === 'COMPLETED' ? 'success' : value === 'IN PROGRESS' ? 'info' : value === 'PENDING' ? 'warning' : value === 'FAILED' ? 'error' : 'default'} size="small" data-id="element-663" />
  }, {
    id: 'type',
    label: 'Type',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['OTC']
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'agentCode',
    label: 'Agent Code',
    filterable: true,
    filterType: 'text' as const,
    format: (value: string) => value || '—'
  }, {
    id: 'createdAt',
    label: 'Created',
    filterable: true,
    filterType: 'date' as const,
    format: (value: string) => value ? new Date(value).toLocaleString() : '—'
  }, {
    id: 'paymentMethod',
    label: 'Payment Method',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['Cash', 'Bank', 'Wallet'],
    format: (value: string) => value || 'Cash'
  }, {
    id: 'status',
    label: 'Legacy Status',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['Completed', 'Rejected', 'Cancelled', 'Pending', 'In-Process'],
    format: (value: string) => <Chip label={value} color={getStatusChipColor(value) as any} size="small" data-id="element-664" />
  }, {
    id: 'receipt',
    label: 'Receipt',
    format: (value: any, row: Transaction) => {
      if (row.status === 'Completed') {
        return <IconButton color="primary" size="small" onClick={e => {
          e.stopPropagation();
          handleReceiptMenuOpen(e, row);
        }} title="Generate Receipt" data-id="element-665">
              <ReceiptIcon data-id="element-666" />
            </IconButton>;
      }
      return <Typography variant="body2" color="text.disabled" data-id="element-667">
            —
          </Typography>;
    }
  }];
  return <Box sx={{
    p: 3,
    bgcolor: 'background.default',
    minHeight: '100vh'
  }} data-id="element-668">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 3
    }} data-id="element-669">
        <Icon icon="lucide:clock" width={28} height={28} color="#0052CC" data-id="element-670" />
        <Typography variant="h4" sx={{
        fontWeight: 700,
        color: '#0052CC'
      }} data-id="element-671">
          Transaction History
        </Typography>
      </Box>

      {isLoading ? <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      py: 10
    }} data-id="element-672">
          <Typography data-id="element-673">Loading transactions...</Typography>
        </Box> : <UnifiedTransactionTable columns={columns} data={transactions} onViewDetails={viewTransactionDetails} title="Transaction List" emptyMessage="No transactions found matching your criteria" data-id="element-674" />}

      {showDetailsModal && selectedTransactionId && <AccordionTransactionModal transactionId={selectedTransactionId} isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} transaction={selectedTransaction} data-id="element-675" />}

      {/* Receipt Menu */}
      <Menu anchorEl={receiptMenuAnchor} open={Boolean(receiptMenuAnchor)} onClose={handleReceiptMenuClose} data-id="element-676">
        <MenuItem onClick={() => handleGenerateReceipt('ePRC')} data-id="element-677">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-678">
            <ReceiptIcon fontSize="small" data-id="element-679" />
            <Typography data-id="element-680">Generate e-PRC Receipt</Typography>
          </Box>
        </MenuItem>

        {selectedTransaction && isMoneyGramTransaction(selectedTransaction.mto) && <MenuItem onClick={() => handleGenerateReceipt('MoneyGram')} data-id="element-681">
              <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-682">
                <ReceiptIcon fontSize="small" data-id="element-683" />
                <Typography data-id="element-684">Generate MoneyGram Receipt</Typography>
              </Box>
            </MenuItem>}

        {selectedTransaction && isBankReceiptAvailable(selectedTransaction.mto) && <MenuItem onClick={() => handleGenerateReceipt('BankReceipt')} data-id="element-685">
              <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-686">
                <ReceiptIcon fontSize="small" data-id="element-687" />
                <Typography data-id="element-688">Generate Bank Receipt</Typography>
              </Box>
            </MenuItem>}
      </Menu>

      {/* Receipt Generator Modal */}
      {showReceiptModal && selectedTransaction && <ReceiptGenerator transaction={selectedTransaction} receiptType={receiptType} isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)} data-id="element-689" />}
    </Box>;
};
export default TransactionHistoryPage;